/**
 * @tennis-matches/core/ko
 * Baut K.O.-Runden für einen bestehenden Schedule (Vorrunde).
 *
 * RUNDENSTRUKTUR (um Vorrundenanzahl nicht zu verändern):
 * - Top 2: 1 Runde (Finale)
 * - Top 4: 2 Runden (HF, Finale+Platz3)
 * - Top 6: 2 Runden (Runde 6: HF 1vs3, 2vs4 + Platz 5vs6 zeitgleich → Runde 7: Finale + optional Platz 3)
 * - Top 8: 3 Runden (VF, HF, Finale+Platz3)
 *
 * Finale und Platz 3 werden in derselben Runde gespielt.
 */

import { KO_TEILNEHMER_VALID, KO_PLACEHOLDER } from "./ko.types.js";
import {
  buildViertelfinale,
  buildHalbfinale4,
  buildHalbfinale8,
  buildFinale,
  buildFinaleRunde,
} from "./ko-pairing.logic.js";

function makeMatchId(r, m) {
  return `r${r + 1}m${m + 1}`;
}

/**
 * Hängt K.O.-Runden an einen Schedule an (in-place).
 * Paarungslogik: Bestes Team vs schlechtestes Team.
 * Platz 3 (kleines Finale) nur wenn playThirdPlace === true.
 *
 * @param {Array<Array<object>>} schedule - Schedule-Array (Vorrunde)
 * @param {2|4|6|8} koTeilnehmer - Anzahl K.O.-Teams (nicht Spieler!)
 * @param {number[]} [courtsNumbers] - Court-Nummern
 * @param {{ playThirdPlace?: boolean }} [options] - playThirdPlace: Platz 3 ausspielen (default: false)
 */
export function appendKoRounds(schedule, koTeilnehmer, courtsNumbers = [], options = {}) {
  if (!Array.isArray(schedule) || !KO_TEILNEHMER_VALID.includes(Number(koTeilnehmer))) return;
  const playThirdPlace = options?.playThirdPlace === true;
  const n = schedule.length;
  const k = Number(koTeilnehmer);
  const courts = Array.isArray(courtsNumbers) && courtsNumbers.length ? courtsNumbers : [1, 2];

  // Top 2: nur Finale (1 Runde, 1 Match)
  if (k === 2) {
    schedule.push(buildFinale(n, makeMatchId, courts[0]));
    return;
  }

  // Top 4: HF → Finale (+ optional Platz 3)
  if (k === 4) {
    schedule.push(buildHalbfinale4(n, makeMatchId, courts));
    schedule.push(playThirdPlace ? buildFinaleRunde(n + 1, makeMatchId, courts) : buildFinale(n + 1, makeMatchId, courts[0]));
    return;
  }

  // Top 8: VF → HF → Finale (+ optional Platz 3)
  if (k === 8) {
    const vfCourts = [courts[0] ?? 1, courts[1] ?? 2, courts[2] ?? 3, courts[3] ?? 4];
    schedule.push(buildViertelfinale(n, makeMatchId, vfCourts));
    schedule.push(buildHalbfinale8(n + 1, makeMatchId, courts));
    schedule.push(playThirdPlace ? buildFinaleRunde(n + 2, makeMatchId, courts) : buildFinale(n + 2, makeMatchId, courts[0] ?? 1));
    return;
  }

  // Top 6: Runde 6 = Halbfinale (1vs3, 2vs4) + Platz 5vs6 zeitgleich → Runde 7 = Finale + optional Platz 3
  if (k === 6) {
    const round6 = [
      { type: "doubles", A: [KO_PLACEHOLDER.RANK, 1], B: [KO_PLACEHOLDER.RANK, 3], id: makeMatchId(n, 0), court: courts[0] ?? 1 },
      { type: "doubles", A: [KO_PLACEHOLDER.RANK, 2], B: [KO_PLACEHOLDER.RANK, 4], id: makeMatchId(n, 1), court: courts[1] ?? 2 },
      { type: "doubles", A: [KO_PLACEHOLDER.RANK, 5], B: [KO_PLACEHOLDER.RANK, 6], id: makeMatchId(n, 2), court: courts[2] ?? 3 },
    ];
    schedule.push(round6);
    schedule.push(playThirdPlace ? buildFinaleRunde(n + 1, makeMatchId, courts) : buildFinale(n + 1, makeMatchId, courts[0] ?? 1));
  }
}
